import React, { useEffect, useState } from "react";
import { TextInput, Button, Stack, Card, Title, Group, Textarea, ActionIcon, Divider, Text, MultiSelect, Loader, Alert, Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateMeeting, useMeeting } from "../hooks";
import { IconPlus, IconTrash, IconAlertCircle } from "@tabler/icons-react";
import { userApi } from "../../../api/users";
import { bidCommitteeApi } from "../../../api/bidCommittee";
import { AgendaTenderDocumentField, type AgendaTenderDocumentValue } from "../components/AgendaTenderDocumentField";

interface AgendaItem {
  subject: string;
  notes: string;
  tenderId: string | null;
  singleSourceId: string | null;
  projectSummarySheetId: string | null;
  tenderDocument: AgendaTenderDocumentValue | null;
}

interface MeetingFormValues {
  title: string;
  location: string;
  plannedStart: Date;
  agendaItems: AgendaItem[];
  memberIds: string[];
}

export const MeetingEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const updateMeeting = useUpdateMeeting();
  const { data: meeting, isLoading, error } = useMeeting(id);
  const [bidCommitteeMembers, setBidCommitteeMembers] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [eligibleTenders, setEligibleTenders] = useState<Array<{ value: string; label: string }>>([]);
  const [eligibleSingleSourceForms, setEligibleSingleSourceForms] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingTenders, setLoadingTenders] = useState(false);
  const [loadingSingleSource, setLoadingSingleSource] = useState(false);
  const [eligiblePss, setEligiblePss] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingPss, setLoadingPss] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(0);

  const form = useForm<MeetingFormValues>({
    initialValues: {
      title: "",
      location: "",
      plannedStart: new Date(),
      agendaItems: [{ subject: '', notes: '', tenderId: null, singleSourceId: null, projectSummarySheetId: null, tenderDocument: null }],
      memberIds: [],
    },
    validate: {
      title: (value) => (!value ? "Title is required" : null),
      location: (value) => (!value ? "Location is required" : null),
    },
  });

  const canEditMeeting =
    meeting?.status === "DRAFT" || meeting?.status === "SCHEDULED";

  // Load meeting data when available
  useEffect(() => {
    if (meeting) {
      if (!canEditMeeting) {
        navigate(`/bid-committee/meetings/${meeting.id}`);
        return;
      }

      form.setValues({
        title: meeting.title,
        location: meeting.location,
        plannedStart: new Date(meeting.plannedStart),
        agendaItems: meeting.agendaItems && meeting.agendaItems.length > 0
          ? meeting.agendaItems.map((item: any) => ({
              subject: item.subject || '',
              notes: item.notes || '',
              tenderId: item.tenderId || null,
              singleSourceId: item.singleSourceId || null,
              projectSummarySheetId: item.projectSummarySheetId || null,
              tenderDocument: item.tenderDocument
                ? { id: item.tenderDocument.id, fileName: item.tenderDocument.fileName }
                : null,
            }))
          : [{ subject: '', notes: '', tenderId: null, singleSourceId: null, projectSummarySheetId: null, tenderDocument: null }],
        memberIds: meeting.attendances?.map((att: any) => att.memberId) || [],
      });
    }
  }, [meeting, navigate, canEditMeeting]);

  // Load bid committee members, tenders, and single source forms on mount
  useEffect(() => {
    loadBidCommitteeMembers();
    loadEligibleTenders();
    loadEligibleSingleSourceForms();
  }, []);

  useEffect(() => {
    if (!meeting) return;
    void loadEligibleProjectSummarySheetsForMeeting();
  }, [meeting?.id]);

  const loadBidCommitteeMembers = async () => {
    setLoadingMembers(true);
    try {
      const users = await userApi.getUsersForEvaluationReport();
      const members = users
        .filter((u) => u.role === 'BID_COMMITTEE_MEMBER')
        .map((u) => ({ value: u.id, label: u.fullName }));
      setBidCommitteeMembers(members);
    } catch (error) {
      console.error('Error loading bid committee members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadEligibleTenders = async () => {
    setLoadingTenders(true);
    try {
      const response = await bidCommitteeApi.getEligibleTenders();
      const tenders = response.data.map((t) => ({
        value: t.id,
        label: `${t.referenceNo} - ${t.description}`,
      }));
      const allTenders = [...tenders];
      const currentAgendaTenders = meeting?.agendaItems?.filter((i: any) => i.tender).map((i: any) => i.tender) || [];
      currentAgendaTenders.forEach((t: any) => {
        if (!tenders.find((te) => te.value === t.id)) {
            allTenders.push({
              value: t.id,
              label: `${t.referenceNo} - ${t.description} (Already selected)`,
            });
        }
      });
      setEligibleTenders(allTenders);
    } catch (error) {
      console.error('Error loading eligible tenders:', error);
    } finally {
      setLoadingTenders(false);
    }
  };

  const loadEligibleProjectSummarySheetsForMeeting = async () => {
    if (!meeting) return;
    setLoadingPss(true);
    try {
      const response = await bidCommitteeApi.getEligibleProjectSummarySheets();
      const sheets = response.data.map((s) => ({
        value: s.id,
        label: `${s.summarySheetNumber} — ${s.projectName}`,
      }));
      const all = [...sheets];
      const currentSheets =
        meeting.agendaItems?.filter((i: any) => i.projectSummarySheet).map((i: any) => i.projectSummarySheet) || [];
      currentSheets.forEach((s: any) => {
        if (!sheets.find((sh) => sh.value === s.id)) {
          all.push({
            value: s.id,
            label: `${s.summarySheetNumber} — ${s.projectName} (current)`,
          });
        }
      });
      setEligiblePss(all);
    } catch (error) {
      console.error("Error loading eligible project summary sheets:", error);
    } finally {
      setLoadingPss(false);
    }
  };

  const loadEligibleSingleSourceForms = async () => {
    setLoadingSingleSource(true);
    try {
      const response = await bidCommitteeApi.getEligibleSingleSourceForms();
      const forms = response.data.map((f) => ({
        value: f.id,
        label: `${f.formNumber} - ${f.projectName ?? f.title} (${f.vendorName})`,
      }));
      const allForms = [...forms];
      const currentAgendaForms = meeting?.agendaItems?.filter((i: any) => i.singleSource).map((i: any) => i.singleSource) || [];
      currentAgendaForms.forEach((s: any) => {
          if (!forms.find((fo) => fo.value === s.id)) {
            allForms.push({
              value: s.id,
              label: `${s.formNumber} - ${s.projectName ?? s.title} (Already selected)`,
            });
          }
      });
      setEligibleSingleSourceForms(allForms);
    } catch (error) {
      console.error('Error loading eligible single source forms:', error);
    } finally {
      setLoadingSingleSource(false);
    }
  };

  const addAgendaItem = () => {
    form.insertListItem('agendaItems', { subject: '', notes: '', tenderId: null, singleSourceId: null, projectSummarySheetId: null, tenderDocument: null });
  };

  const removeAgendaItem = (index: number) => {
    form.removeListItem('agendaItems', index);
  };

  const handleSubmit = (values: MeetingFormValues) => {
    if (!id) return;

    updateMeeting.mutate(
      {
        id,
        data: {
          title: values.title,
          location: values.location,
          plannedStart: values.plannedStart.toISOString(),
          agendaItems: values.agendaItems.map((item, index) => ({
            orderNo: index + 1,
            subject: item.subject,
            notes: item.notes || null,
            tenderId: item.tenderId || null,
            singleSourceId: item.singleSourceId || null,
            projectSummarySheetId: item.projectSummarySheetId || null,
            tenderDocumentId: item.tenderDocument?.id || null,
          })),
          memberIds: values.memberIds,
        },
      },
      {
        onSuccess: () => {
          navigate(`/bid-committee/meetings/${id}`);
        },
      }
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !meeting) {
    return <div>Meeting not found or error loading meeting</div>;
  }

  if (!canEditMeeting) {
    return (
      <Alert color="red" title="Cannot Edit">
        Meetings can only be edited until they are started.
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>Edit Meeting: {meeting.meetingNumber}</Title>

      {meeting.status === "SCHEDULED" && (
        <Alert color="blue" title="Scheduled meeting">
          If you change the date, location, title, agenda, or selected members, each invited
          bid committee member will receive an email with an updated calendar attachment.
        </Alert>
      )}

      <Card>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Title"
              placeholder="e.g., Bid Committee #1/23"
              required
              {...form.getInputProps("title")}
            />

            <Group grow>
              <TextInput
                label="Location"
                placeholder="e.g., Board Room"
                required
                {...form.getInputProps("location")}
              />
              <DatePickerInput
                label="Planned Start"
                placeholder="Select date and time"
                required
                {...form.getInputProps("plannedStart")}
              />
            </Group>

            <Divider label="Bid Committee Members" labelPosition="center" my="md" />

            {loadingMembers ? (
              <Loader size="md" />
            ) : (
              <>
                {bidCommitteeMembers.length === 0 && (
                  <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                    No bid committee members found. Please add users with BID_COMMITTEE_MEMBER role.
                  </Alert>
                )}
                <MultiSelect
                  label="Bid Committee Members"
                  placeholder="Select bid committee members"
                  data={bidCommitteeMembers}
                  value={form.values.memberIds}
                  onChange={(value) => form.setFieldValue('memberIds', value)}
                  searchable
                  clearable
                />
              </>
            )}

            <Divider label="Agenda Items" labelPosition="center" my="md" />

            <Stack gap="md">
              {form.values.agendaItems.map((item, index) => (
                <Card key={index} shadow="xs" p="md" withBorder>
                  <Group justify="space-between" mb="md">
                    <Text fw={500}>Agenda Item {index + 1}</Text>
                    <ActionIcon
                      color="red"
                      variant="light"
                      disabled={uploadingDocuments > 0}
                      onClick={() => removeAgendaItem(index)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                  <Stack gap="md">
                    <Textarea
                      label="Agenda Subject"
                      description=""
                      placeholder="Write agenda subject"
                      required
                      minRows={3}
                      value={item.subject}
                      onChange={(e) => {
                        const updatedItems = [...form.values.agendaItems];
                        updatedItems[index] = { ...updatedItems[index], subject: e.currentTarget.value };
                        form.setFieldValue('agendaItems', updatedItems);
                      }}
                      error={form.errors[`agendaItems.${index}.subject` as keyof typeof form.errors]}
                    />
                    <Textarea
                      label="Presented Information / Notes"
                      description=""
                      placeholder="Write notes or presented information."
                      minRows={6}
                      value={item.notes}
                      onChange={(e) => {
                        const updatedItems = [...form.values.agendaItems];
                        updatedItems[index] = { ...updatedItems[index], notes: e.currentTarget.value };
                        form.setFieldValue('agendaItems', updatedItems);
                      }}
                      error={form.errors[`agendaItems.${index}.notes` as keyof typeof form.errors]}
                    />
                    {loadingTenders ? <Loader size="sm" /> : (
                      <Select
                        label="Linked Tender (Optional)"
                        placeholder="Select a tender for this agenda item"
                        data={eligibleTenders}
                        value={item.tenderId}
                        onChange={(value) => {
                          const updatedItems = [...form.values.agendaItems];
                          updatedItems[index] = { ...updatedItems[index], tenderId: value };
                          form.setFieldValue('agendaItems', updatedItems);
                        }}
                        searchable
                        clearable
                      />
                    )}
                    {loadingSingleSource ? <Loader size="sm" /> : (
                      <Select
                        label="Linked Single Source Form (Optional)"
                        placeholder="Select a single source form for this agenda item"
                        data={eligibleSingleSourceForms}
                        value={item.singleSourceId}
                        onChange={(value) => {
                          const updatedItems = [...form.values.agendaItems];
                          updatedItems[index] = { ...updatedItems[index], singleSourceId: value };
                          form.setFieldValue('agendaItems', updatedItems);
                        }}
                        searchable
                        clearable
                      />
                    )}
                    {loadingPss ? <Loader size="sm" /> : (
                      <Select
                        label="Linked Project Summary Sheet PR-03 (Optional)"
                        placeholder="Approved PR-03"
                        data={eligiblePss}
                        value={item.projectSummarySheetId}
                        onChange={(value) => {
                          const updatedItems = [...form.values.agendaItems];
                          updatedItems[index] = { ...updatedItems[index], projectSummarySheetId: value };
                          form.setFieldValue("agendaItems", updatedItems);
                        }}
                        searchable
                        clearable
                      />
                    )}
                    <AgendaTenderDocumentField
                      value={item.tenderDocument}
                      onChange={(doc) => {
                        const updatedItems = [...form.values.agendaItems];
                        updatedItems[index] = { ...updatedItems[index], tenderDocument: doc };
                        form.setFieldValue("agendaItems", updatedItems);
                      }}
                      onUploadingChange={(uploading) =>
                        setUploadingDocuments((count) => count + (uploading ? 1 : -1))
                      }
                    />
                  </Stack>
                </Card>
              ))}
              <Button
                leftSection={<IconPlus size={16} />}
                variant="light"
                onClick={addAgendaItem}
              >
                Add Agenda Item
              </Button>
            </Stack>

            <Group justify="space-between" mt="xl">
              <Button
                variant="outline"
                onClick={() => navigate(`/bid-committee/meetings/${id}`)}
              >
                Cancel
              </Button>
              <Button type="submit" loading={updateMeeting.isPending} disabled={uploadingDocuments > 0}>
                Update Meeting
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
};

